import React from 'react';
import { useNavigate } from 'react-router-dom';
import '../CSS/ExecPlacement.css';

const ExecPlacement: React.FC = () => {
  const navigate = useNavigate();
  const imagePath = `${process.env.PUBLIC_URL || ''}/assets/Web Version/Services/Executive Search/executive-search-banner-image.png`;
  
  return (
    <div className="exec-place-page">
      <div className="exec-place-hero-banner">
        <div 
          className="exec-place-hero-background"
          style={{ 
            backgroundImage: `url("${imagePath}")` 
          }}
        ></div>
        <div className="exec-place-hero-content">
          <p className="exec-place-breadcrumb">Our Services / Executive Search and Placement Services</p>
          <h1 className="exec-place-title">Executive Search & Placement Services for Higher Education and Nonprofits</h1>
        </div>
      </div>

      <div className="exec-place-body">
        <div className="exec-place-intro">
          <p>
            Myers McRae Executive Search and Consulting has specialized in recruiting exceptional leaders for higher education institutions and nonprofit organizations nationwide. Our success is built on experience, integrity, and a deep understanding of the academic and nonprofit landscapes.
          </p>
          <p>
            Our senior consultants bring proven executive-level experience in both higher education and consulting. Under their leadership, Myers McRae has earned a national reputation for excellence, consistently identifying and recruiting talented leaders who bring <strong>vision and impact</strong> to the institutions they serve.
          </p>
        </div>
      </div>

      <div className="exec-commitment">
        <h2 className="exec-commitment-title">Our Commitment to Your Success</h2>
        <p className="exec-commitment-subtitle">
          We are dedicated to your success from start to finish. Our team works tirelessly until a candidate of choice is identified and hired. Our commitment includes two core guarantees:
        </p>
        <div className="exec-commitment-guarantees">
          <div className="exec-commitment-guarantee">
            <div className="exec-commitment-icon">
              <img 
                src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Services/Executive Search/no-time-limit-icon.svg`}
                alt="No Time Limit"
              />
            </div>
            <h3 className="exec-commitment-guarantee-title">No Time Limit</h3>
            <p className="exec-commitment-guarantee-description">
              If the search committee requests additional recruitment beyond the original timeline, we <b>continue our efforts at no additional cost.</b>
            </p>
          </div>
          <div className="exec-commitment-guarantee">
            <div className="exec-commitment-icon">
              <img 
                src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Services/Executive Search/12-month-replacement-guarantee-icon.svg`}
                alt="12-Month Replacement Guarantee"
              />
            </div>
            <h3 className="exec-commitment-guarantee-title">12-Month Replacement Guarantee</h3>
            <p className="exec-commitment-guarantee-description">
              In the unlikely event that a selected candidate leaves within 12 months, Myers McRae will conduct a <strong>replacement search on an expense-only basis</strong>—a reflection of our commitment to long-term results.
            </p>
          </div>
        </div>
      </div>

      <div className="exec-text-image-pair">
        <div className="exec-text-image-pair-container">
          <div className="exec-text-image-pair-image">
            <img 
              src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Services/Executive Search/transparent-fixed-fee-approach-image.png`}
              alt="Transparent Fixed Fee Approach"
            />
          </div>
          <div className="exec-text-image-pair-text">
            <h2 className="exec-text-image-pair-title">A Transparent, Fixed-Fee Approach</h2>
            <p className="exec-text-image-pair-description">
              Say goodbye to financial surprises. Our professional fee is <b>fixed</b> and includes all <b>search services and administrative expenses.</b> Regardless of the candidate's final salary, your cost remains the same.
            </p>
          </div>
        </div>
      </div>

      <div className="exec-comprehensive-pack">
        <h2 className="exec-comprehensive-pack-title">Comprehensive Service Package</h2>
        <p className="exec-comprehensive-pack-subtitle">
          Our all-inclusive fee covers every step of the process, including:
        </p>
        <div className="exec-comprehensive-pack-list">
          <div className="exec-comprehensive-pack-column">
            <ul>
              <li>Development and design of a detailed <strong>Position Description Profile</strong>.</li>
              <li>Email distribution to our <strong>150,000+ contact network</strong> of prospective candidates.</li>
              <li><strong>Strategic advertising</strong> design and placement.</li>
              <li><strong>Active outreach</strong> and candidate recruitment.</li>
              <li>Solicitation of nominations and referrals.</li>
              <li><strong>Ongoing progress updates</strong> throughout the process.</li>
              <li>Preliminary interviews with qualified applicants.</li>
              <li>Identification and presentation of <strong>top candidates</strong>.</li>
            </ul>
          </div>
          <div className="exec-comprehensive-pack-column">
            <ul>
              <li>Search committee supports evaluation and selection.</li>
              <li>Coordination and facilitation of interviews.</li>
              <li><strong>Background, reference, and credential checks</strong>.</li>
              <li>Candidate communication and final notifications.</li>
              <li>Contract negotiation assistance (if requested).</li>
              <li><strong>Secure online access</strong> to candidate materials via our web-based Resume File platform.</li>
              <li><strong>All office operations</strong>, including printing, postage, courier fees, and toll-free conference calls— are included in the professional fee.</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="exec-proven-checklist">
        <h2 className="exec-proven-checklist-title">A Proven, Collaborative Process</h2>
        <p className="exec-proven-checklist-subtitle">
          From developing your customized timeline to final placement, we partner closely with your selection committee <strong>every step of the way</strong>. Our structured, data-informed process ensures <strong>transparency, consistency, and exceptional outcomes</strong>, helping your institution identify leaders who will shape its future with <strong>vision and integrity</strong>.
        </p>
        
        <div className="exec-proven-checklist-timeline">
          <div className="exec-proven-checklist-line"></div>
          <div className="exec-proven-checklist-dots">
            <div className="exec-proven-checklist-dot"></div>
            <div className="exec-proven-checklist-dot"></div>
            <div className="exec-proven-checklist-dot"></div>
            <div className="exec-proven-checklist-dot"></div>
          </div>
        </div>

        <div className="exec-proven-checklist-phases">
          <div className="exec-proven-checklist-phase">
            <div className="exec-proven-checklist-phase-header">
              <span className="exec-proven-checklist-phase-number">PHASE 1</span>
              <h3 className="exec-proven-checklist-phase-title">Assess & Plan</h3>
            </div>
            <ul className="exec-proven-checklist-phase-list">
              <li>
                <img src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Icons/green-tick-icon.svg`} alt="Check" className="exec-proven-checklist-check" />
                Conduct Interviews with Key Stakeholders
              </li>
              <li>
                <img src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Icons/green-tick-icon.svg`} alt="Check" className="exec-proven-checklist-check" />
                Confirm Timeline
              </li>
              <li>
                <img src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Icons/green-tick-icon.svg`} alt="Check" className="exec-proven-checklist-check" />
                Prepare Profile
              </li>
              <li>
                <img src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Icons/green-tick-icon.svg`} alt="Check" className="exec-proven-checklist-check" />
                Review Confidentiality Guidelines
              </li>
              <li>
                <img src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Icons/green-tick-icon.svg`} alt="Check" className="exec-proven-checklist-check" />
                Initiate Search
              </li>
            </ul>
          </div>

          <div className="exec-proven-checklist-phase">
            <div className="exec-proven-checklist-phase-header">
              <span className="exec-proven-checklist-phase-number">PHASE 2</span>
              <h3 className="exec-proven-checklist-phase-title">Recruit & Evaluate</h3>
            </div>
            <ul className="exec-proven-checklist-phase-list">
              <li>
                <img src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Icons/green-tick-icon.svg`} alt="Check" className="exec-proven-checklist-check" />
                Identify potential candidates using Myers McRae Proprietary Database
              </li>
              <li>
                <img src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Icons/green-tick-icon.svg`} alt="Check" className="exec-proven-checklist-check" />
                Place Advertising
              </li>
              <li>
                <img src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Icons/green-tick-icon.svg`} alt="Check" className="exec-proven-checklist-check" />
                Monitor Candidate Submissions
              </li>
              <li>
                <img src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Icons/green-tick-icon.svg`} alt="Check" className="exec-proven-checklist-check" />
                Pursue Referrals
              </li>
            </ul>
          </div>

          <div className="exec-proven-checklist-phase">
            <div className="exec-proven-checklist-phase-header">
              <span className="exec-proven-checklist-phase-number">PHASE 3</span>
              <h3 className="exec-proven-checklist-phase-title">Select & Interview Semi-finalists</h3>
            </div>
            <ul className="exec-proven-checklist-phase-list">
              <li>
                <img src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Icons/green-tick-icon.svg`} alt="Check" className="exec-proven-checklist-check" />
                Myers McRae screens candidates
              </li>
              <li>
                <img src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Icons/green-tick-icon.svg`} alt="Check" className="exec-proven-checklist-check" />
                Provides committee with recommended candidates
              </li>
              <li>
                <img src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Icons/green-tick-icon.svg`} alt="Check" className="exec-proven-checklist-check" />
                Open online review platform for committee to assess candidates
              </li>
              <li>
                <img src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Icons/green-tick-icon.svg`} alt="Check" className="exec-proven-checklist-check" />
                Organize & conduct online interviews
              </li>
              <li>
                <img src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Icons/green-tick-icon.svg`} alt="Check" className="exec-proven-checklist-check" />
                Sample Interview Questions provided
              </li>
              <li>
                <img src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Icons/green-tick-icon.svg`} alt="Check" className="exec-proven-checklist-check" />
                Facilitate final candidate selection
              </li>
            </ul>
          </div>

          <div className="exec-proven-checklist-phase">
            <div className="exec-proven-checklist-phase-header">
              <span className="exec-proven-checklist-phase-number">PHASE 4</span>
              <h3 className="exec-proven-checklist-phase-title">Negotiate, Hire & Conclude</h3>
            </div>
            <ul className="exec-proven-checklist-phase-list">
              <li>
                <img src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Icons/green-tick-icon.svg`} alt="Check" className="exec-proven-checklist-check" />
                Organize Onsite Interviews
              </li>
              <li>
                <img src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Icons/green-tick-icon.svg`} alt="Check" className="exec-proven-checklist-check" />
                Conduct Background Checks
              </li>
              <li>
                <img src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Icons/green-tick-icon.svg`} alt="Check" className="exec-proven-checklist-check" />
                Final selection and acceptance
              </li>
              <li>
                <img src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Icons/green-tick-icon.svg`} alt="Check" className="exec-proven-checklist-check" />
                Close Search
              </li>
              <li>
                <img src={`${process.env.PUBLIC_URL || ''}/assets/Web Version/Icons/green-tick-icon.svg`} alt="Check" className="exec-proven-checklist-check" />
                Announce acceptance
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="exec-contact">
        <h2 className="exec-contact-title">Ready to Find Your Next Visionary Leader?</h2>
        <p className="exec-contact-subtitle">
          Partner with Myers McRae to secure the exceptional leadership your institution deserves.
        </p>
        <div className="exec-contact-buttons">
          <button 
            className="exec-contact-button exec-contact-button-primary"
            onClick={() => navigate('/contact-us')}
          >
            Contact Us Today
          </button>
          <button 
            className="exec-contact-button exec-contact-button-secondary"
            onClick={() => navigate('/news-and-insights/recent-placements')}
          >
            View Our Recent Placements
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExecPlacement;

